import React, { useEffect, useRef } from "react";
import { gsap } from "../../animations/gsap.config";
import { ScrollTrigger } from "../../animations/gsap.config";
import portfolioData from "../../data/portfolio.json";
import { BarChart3, TrendingUp, Users, Target } from "lucide-react";

const ICON_MAP = {
  coverage: Target,
  enumerators: Users,
  data: BarChart3,
  satisfaction: TrendingUp,
};

const ImpactMetrics = () => {
  const { metrics } = portfolioData;
  const sectionRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        cardsRef.current,
        { y: 50, opacity: 0, scale: 0.95 },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          stagger: 0.15,
          duration: 0.8,
          ease: "back.out(1.2)",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 75%",
          },
        },
      );

      /* Number counter animation */
      cardsRef.current.forEach((card) => {
        const numberElement = card.querySelector(".metric-number");
        if (!numberElement) return;

        const targetValue = parseInt(numberElement.getAttribute("data-value"));

        gsap.fromTo(
          numberElement,
          { textContent: 0 },
          {
            textContent: targetValue,
            duration: 2,
            ease: "power2.out",
            snap: { textContent: 1 },
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top 75%",
            },
          },
        );
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-10 bg-white relative border-t border-slate-200 overflow-hidden"
    >
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary-600/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container-fluid px-4 md:px-8 max-w-7xl mx-auto relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-16 md:mb-20">
          <h2 className="font-display font-black text-4xl md:text-5xl text-slate-800 tracking-tight leading-none mb-4">
            {metrics.headline}{" "}
            <span className="text-gradient-primary">
              {metrics.headlineAccent}
            </span>
          </h2>
          <p className="text-slate-600 text-base md:text-lg max-w-xl mx-auto">
            {metrics.description}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {metrics.stats.map((stat, i) => {
            const IconC = ICON_MAP[stat.id] || Target;
            return (
              <div
                key={stat.id}
                ref={(el) => (cardsRef.current[i] = el)}
                className="glass rounded-3xl p-8 border border-slate-200 relative group overflow-hidden bg-white hover:bg-slate-50 transition-colors duration-500 shadow-lg"
              >
                {/* Hover Glow Component */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary-600/0 to-primary-600/0 group-hover:from-primary-600/5 group-hover:to-secondary-500/5 transition-colors duration-500 z-0" />

                <div className="relative z-10">
                  <div className="w-12 h-12 rounded-2xl bg-slate-100 border border-slate-200 flex items-center justify-center mb-6 text-slate-600 group-hover:text-primary-600 group-hover:border-primary-500/30 transition-all duration-300">
                    <IconC size={24} />
                  </div>

                  <div className="flex items-baseline gap-1 mb-2">
                    <span
                      className="font-mono font-black text-5xl md:text-6xl text-slate-800 metric-number"
                      data-value={stat.value}
                    >
                      0
                    </span>
                    <span className="font-mono font-bold text-3xl text-secondary-400">
                      {stat.suffix}
                    </span>
                  </div>

                  <h3 className="font-bold text-lg text-slate-800 mb-2">
                    {stat.label}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed font-light line-clamp-3">
                    {stat.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ImpactMetrics;
